<script setup>
import OverviewReportFilters from './OverviewReportFilters.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/vue-table';
import {
  computed,
  onMounted,
  ref,
  h,
  withDirectives,
  resolveDirective,
} from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
import { useI18n } from 'vue-i18n';
import SummaryReportLink from './SummaryReportLink.vue';

const flagMap = {
  agent: 'isFetchingAgentSummaryReports',
  inbox: 'isFetchingInboxSummaryReports',
  team: 'isFetchingTeamSummaryReports',
  label: 'isFetchingLabelSummaryReports',
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(() => uiFlags.value[flagMap[props.type]] ?? false);

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter([props.summaryKey]) || [];

const getMetrics = id =>
  reportMetrics.value.find(metrics => metrics.id === Number(id)) || {};
const columnHelper = createColumnHelper();
const { t } = useI18n();

// Renderiza a celula de uma metrica numerica usando o valor FORMATADO
// guardado em row.original (ex.: '2m 30s' / '1,234' / '--'). A ordenacao
// usa o valor BRUTO via accessorFn, entao display e sort ficam desacoplados.
const metricCell = (displayKey, extraClassFn) => cellProps => {
  const value = cellProps.row.original[displayKey];
  const hasValue = value && value !== '--';
  const extraClass = extraClassFn ? extraClassFn(cellProps.row.original) : '';
  return h(
    'span',
    {
      class: [hasValue ? '' : 'text-n-slate-10', extraClass]
        .filter(Boolean)
        .join(' '),
    },
    value
  );
};

// Cabecalho de coluna com icone de definicao (tooltip). Reusa o padrao
// <span class="i-ri-information-line" v-tooltip /> do ReportMetricCard. Como
// o header e renderizado via FlexRender/h(), a diretiva global 'tooltip'
// (floating-vue) e aplicada com withDirectives + resolveDirective.
const headerWithInfo = (labelKey, infoKey) => () =>
  h('span', { class: 'flex items-center gap-1' }, [
    h('span', t(labelKey)),
    withDirectives(
      h('span', {
        class: 'i-ri-information-line size-3.5 text-n-slate-10 shrink-0',
      }),
      [[resolveDirective('tooltip'), t(infoKey)]]
    ),
  ]);

// Destaque de quartil para a coluna de 1a resposta: o quartil mais rapido
// (menor tempo) fica verde, o mais lento fica vermelho. Limiares calculados
// sobre os valores brutos das linhas que tem dado.
const firstResponseThresholds = computed(() => {
  const values = tableData.value
    .map(row => row.avgFirstResponseTimeRaw)
    .filter(v => v != null && v > 0)
    .sort((a, b) => a - b);
  if (values.length < 4) return null;
  const quantile = q => values[Math.floor((values.length - 1) * q)];
  return { fast: quantile(0.25), slow: quantile(0.75) };
});

const firstResponseQuartileClass = row => {
  const thresholds = firstResponseThresholds.value;
  const raw = row.avgFirstResponseTimeRaw;
  if (!thresholds || raw == null || raw <= 0) return '';
  if (raw <= thresholds.fast) return 'text-n-teal-11';
  if (raw >= thresholds.slow) return 'text-n-ruby-11';
  return '';
};

// sortUndefined: 'last' empurra linhas sem metrica para o fim em asc e desc.
const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t(`SUMMARY_REPORTS.${props.type.toUpperCase()}`),
    width: 300,
    cell: cellProps => h(SummaryReportLink, cellProps),
    sortingFn: 'alphanumeric',
  }),
  columnHelper.accessor(row => row.conversationsCountRaw, {
    id: 'conversationsCount',
    header: t('SUMMARY_REPORTS.CONVERSATIONS'),
    width: 200,
    cell: metricCell('conversationsCount'),
    sortingFn: 'basic',
    sortUndefined: 'last',
  }),
  columnHelper.accessor(row => row.avgFirstResponseTimeRaw, {
    id: 'avgFirstResponseTime',
    header: headerWithInfo(
      'SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME',
      'SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME_INFO'
    ),
    width: 200,
    cell: metricCell('avgFirstResponseTime', firstResponseQuartileClass),
    sortingFn: 'basic',
    sortUndefined: 'last',
  }),
  columnHelper.accessor(row => row.avgResolutionTimeRaw, {
    id: 'avgResolutionTime',
    header: headerWithInfo(
      'SUMMARY_REPORTS.AVG_RESOLUTION_TIME',
      'SUMMARY_REPORTS.AVG_RESOLUTION_TIME_INFO'
    ),
    width: 200,
    cell: metricCell('avgResolutionTime'),
    sortingFn: 'basic',
    sortUndefined: 'last',
  }),
  columnHelper.accessor(row => row.avgReplyTimeRaw, {
    id: 'avgReplyTime',
    header: headerWithInfo(
      'SUMMARY_REPORTS.AVG_REPLY_TIME',
      'SUMMARY_REPORTS.AVG_REPLY_TIME_INFO'
    ),
    width: 200,
    cell: metricCell('avgReplyTime'),
    sortingFn: 'basic',
    sortUndefined: 'last',
  }),
  columnHelper.accessor(row => row.resolutionsCountRaw, {
    id: 'resolutionsCount',
    header: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
    width: 200,
    cell: metricCell('resolutionsCount'),
    sortingFn: 'basic',
    sortUndefined: 'last',
  }),
]);

const renderAvgTime = value => (value ? formatTime(value) : '--');

const renderCount = value => (value ? value.toLocaleString() : '--');

const tableData = computed(() =>
  rowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    const {
      conversationsCount,
      avgFirstResponseTime,
      avgResolutionTime,
      avgReplyTime,
      resolvedConversationsCount,
    } = rowMetrics;
    return {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      conversationsCount: renderCount(conversationsCount),
      avgFirstResponseTime: renderAvgTime(avgFirstResponseTime),
      avgReplyTime: renderAvgTime(avgReplyTime),
      avgResolutionTime: renderAvgTime(avgResolutionTime),
      resolutionsCount: renderCount(resolvedConversationsCount),
      // valores brutos (numero ou undefined) usados SO para ordenacao/quartil
      conversationsCountRaw: conversationsCount ?? undefined,
      avgFirstResponseTimeRaw: avgFirstResponseTime ?? undefined,
      avgReplyTimeRaw: avgReplyTime ?? undefined,
      avgResolutionTimeRaw: avgResolutionTime ?? undefined,
      resolutionsCountRaw: resolvedConversationsCount ?? undefined,
    };
  })
);

const fetchReportsWithRetry = async () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  try {
    await store.dispatch(props.actionKey, params);
  } catch {
    try {
      await store.dispatch(props.actionKey, params);
    } catch {
      useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
    }
  }
};

const fetchAllData = () => {
  store.dispatch(props.fetchItemsKey);
  fetchReportsWithRetry();
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: true,
  enableSortingRemoval: false,
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  initialState: {
    sorting: [{ id: 'avgFirstResponseTime', desc: false }],
  },
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <OverviewReportFilters
    :disabled="isLoading"
    @filter-change="onFilterChange"
  />
  <div
    class="relative flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2"
  >
    <Table :table="table" />
    <Transition
      enter-active-class="transition-opacity duration-300 ease-out"
      leave-active-class="transition-opacity duration-200 ease-in"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isLoading"
        class="absolute inset-0 flex justify-center pt-[12.5rem] bg-n-solid-1/70 rounded-2xl pointer-events-none"
      >
        <Spinner :size="32" class="text-n-brand" />
      </div>
    </Transition>
  </div>
</template>
